import TileMinedLogic from "../../../logic/TileMinedLogic";
import { Settings } from "../../../Settings";
import ValueCalculator from "../../../util/ValueCalculator";
import Character from "../../character/Character";
import GridTile from "../../grid/GridTile";
import WorldGrid from "../../grid/WorldGrid";
import Vector from "../../math/Vector";
import NumberValue from "../../value/NumberValue";
import LineBasedSkill from "../LineBasedSkill";
import SkillUtil from "../SkillUtil";

const HALF_PI = Math.PI / 2;

export default class Drill extends LineBasedSkill {
    /**
     * @param {WorldGrid} worldGrid
     * @param {TileMinedLogic} tileMinedLogic
     * @param {NumberValue} depthUpgrades
     */
    constructor(worldGrid, tileMinedLogic, depthUpgrades) {
        super(worldGrid);

        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;

        /**
         * @constant
         * @type {NumberValue}
         */
        this.depthUpgradeCount = depthUpgrades;

        /**
         * @type {GridTile}
         */
        this.drillTile = null;
        /**
         * @type {GridTile}
         */
        this.goalTile = null;
        /**
         * @type {boolean}
         */
        this.skillActive = false;
    }

    onSkillActivation() {
        this.skillActive = true;
    }

    onSkillDeactivation() {
        this.skillActive = false;
        this.drillTile = null;
    }

    /**
     * @param {Character} miner
     * @param {number} frameTimeRatio
     */
    updateForFrame(miner, frameTimeRatio) {

        // Move the center from the origin to the center of the tile.
        this.startPoint.copy(miner.position.worldCoordinateOrigin);
        this.startPoint.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

        // project a line from the tile out along the current angle.

        // The default range is 1 tile, but we don't want to count the tile the miner is standing in. Add 1.
        const pixelRange = (Settings.skill.drill.minRange + (Settings.skill.drill.tilesPerUpgrade * this.depthUpgradeCount.getValue())) * Settings.tile.size;

        this.workingPosition.copy(this.startPoint);
        this.workingPosition.y += pixelRange;

        this.goalTile = this.worldGrid.findGridTile(this.workingPosition);
        this.drillTile = this.goalTile;
        if (!this.goalTile) {
            return;
        }

        // Project along the line to find what tile is hit
        this.drillTile = this.findFirstClosedTileOnLine(this.startPoint, HALF_PI, pixelRange);
        if (!this.drillTile) {
            // Use the goal tile as the end point.
            this.endPoint.copy(this.goalTile.origin);
            this.endPoint.x = this.startPoint.x;
            this.endPoint.y += Settings.tile.halfSize;

            return;
        }

        // If we are hitting it with a laser, then we should be able to see it.
        this.drillTile.markTileAsCurrentlyVisible();

        // Set the start/end positions of the laser (for the benefit of the canvas panel)
        this.endPoint.copy(this.drillTile.origin);
        this.endPoint.x = this.startPoint.x;
        this.endPoint.y += Settings.tile.halfSize;

        if (!this.drillTile.open) {
            this.tileMinedLogic.drillTileForFrame(this.drillTile, frameTimeRatio);
        }
    }

    /**
     * @private
     * @param {Vector} startPoint 
     * @param {number} radians 
     * @param {number} pixelRange 
     * @returns {GridTile}
     */
    findFirstClosedTileOnLine(startPoint, radians, pixelRange) {

        let prevTile = null;
        for (let radius = Settings.tile.halfSize; radius <= pixelRange; radius += Settings.tile.halfSize) {

            const position = SkillUtil.projectPosition(startPoint, radius, radians, this.workingPosition);

            const projectedTile = this.getProjectedTile(prevTile, position);
            if (!projectedTile) {
                break;
            }

            // See if we've already checked this tile
            if (prevTile === projectedTile) {
                continue;
            }

            // Any tile touched by the drill should become visible.
            if (!projectedTile.lighting.everVisibleToCharacter) {
                projectedTile.markTileAsCurrentlyVisible();
            }

            // See if we've reached a closed tile
            if (!projectedTile.open) {
                return projectedTile;
            }

            prevTile = projectedTile; // no. projected tile is open
        }

        return (prevTile && !prevTile.open) ? prevTile : null;
    }    
}